import { useState } from "react";
import {
  Grid,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import color from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useUsersTable } from "../../Query/useUsers";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";

interface UsersTableProps {
  workspaceId: number;
}

const UsersTable: React.FC<UsersTableProps> = ({ workspaceId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("workspaces");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [search, setSearch] = useState("");
  const { colors: workspace } = useWorkspaceColors();

  const { data, isLoading } = useUsersTable(
    page,
    pageSize,
    search,
    workspaceId,
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  if (isLoading || !data) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.secondary}
      />
    );
  } else {
    return (
      <Grid container direction="column" alignItems="center">
        <Grid>
          <Input
            style={{
              color: color.primary_dark,
              textAlign: "center",
            }}
            inputProps={{
              "aria-label": "search",
              style: {
                fontSize: "1.1rem",
              },
            }}
            placeholder={t("searchUsersPlaceholder")}
            value={search}
            onChange={(event) => {
              setPage(1);
              setSearch(event.target.value);
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          />
        </Grid>
        <Grid item mt={3} sx={{ maxWidth: "100%", width: "100%" }}>
          <TableContainer
            sx={{
              border: `1px solid ${color.gray300}`,
              borderRadius: 2,
              backgroundColor: color.gray50,
              overflow: "hidden",
            }}
          >
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      color: color.primary_dark,
                      fontWeight: "bolder",
                      width: "10%",
                    }}
                  >
                    {t("table_index")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.primary_dark,
                      fontWeight: "bolder",
                      width: "25%",
                    }}
                  >
                    {t("table_name")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.primary_dark,
                      fontWeight: "bolder",
                      width: "30%",
                    }}
                  >
                    {t("table_surname")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.primary_dark,
                      fontWeight: "bolder",
                      width: "35%",
                    }}
                  >
                    {t("table_email")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.users && data.users.length > 0 ? (
                  data.users.map((user) => (
                    <TableRow
                      key={user.id}
                      onClick={() => navigate(`/user/${user.id}`)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: color.gray100,
                        },
                      }}
                    >
                      <TableCell sx={{ color: color.primary_dark }}>
                        {user.id}
                      </TableCell>
                      <TableCell sx={{ color: color.primary_dark }}>
                        {user.name}
                      </TableCell>
                      <TableCell sx={{ color: color.primary_dark }}>
                        {user.surname}
                      </TableCell>
                      <TableCell sx={{ color: color.primary_dark }}>
                        {user.email}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="left"
                      sx={{ color: color.primary_dark }}
                    >
                      {t("noUsersAvailable")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={data.total}
            rowsPerPage={pageSize}
            page={page - 1}
            onPageChange={handleChangePage}
            sx={{
              color: color.primary_dark,
              "& .MuiTablePagination-toolbar": {
                color: color.primary_dark,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  color: color.primary_dark,
                },
              "& .MuiIconButton-root": {
                color: color.primary_dark,
              },
            }}
          />
        </Grid>
      </Grid>
    );
  }
};

export default UsersTable;
